import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { ShippingAddress } from '@flashcard/shared';
import type { QuoteResponse } from '../lib/api';

interface OrderData {
  customer: { email: string; firstName: string; lastName: string } | null;
  shippingAddress: ShippingAddress | null;
  items: Array<{ productUid: string; quantity: number; fileKey: string; fileUrl: string }>;
  quote: QuoteResponse | null;
}

interface OrderState extends OrderData {
  setCustomer: (c: OrderData['customer']) => void;
  setShippingAddress: (a: ShippingAddress) => void;
  addItem: (item: OrderData['items'][0]) => void;
  removeItem: (index: number) => void;
  setQuote: (q: QuoteResponse) => void;
  clearOrder: () => void;
}

const initialState: OrderData = {
  customer: null,
  shippingAddress: null,
  items: [],
  quote: null,
};

const OrderContext = createContext<OrderState | null>(null);

export function OrderProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OrderData>(initialState);

  const setCustomer = useCallback((c: OrderData['customer']) => setState((s) => ({ ...s, customer: c })), []);
  const setShippingAddress = useCallback(
    (a: ShippingAddress) => setState((s) => ({ ...s, shippingAddress: a })),
    [],
  );
  const addItem = useCallback(
    (item: OrderData['items'][0]) => setState((s) => ({ ...s, items: [...s.items, item] })),
    [],
  );
  const removeItem = useCallback(
    (i: number) => setState((s) => ({ ...s, items: s.items.filter((_, idx) => idx !== i) })),
    [],
  );
  const setQuote = useCallback((q: QuoteResponse) => setState((s) => ({ ...s, quote: q })), []);
  const clearOrder = useCallback(() => setState(initialState), []);

  return (
    <OrderContext.Provider
      value={{ ...state, setCustomer, setShippingAddress, addItem, removeItem, setQuote, clearOrder }}
    >
      {children}
    </OrderContext.Provider>
  );
}

export function useOrder() {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error('useOrder must be used within OrderProvider');
  return ctx;
}
